import Rx from "rxjs";
import { LayersState } from "../../../../rootState";
import { FeatureLayersMappingServiceResponse, gisServerMaxFeatureCountSelector } from "../../../../api/api-packages";
import { EpicFactory } from "../../../../core/core-redux/reduxTypes";
import { STORE_WFS_FEATURE_COUNT } from "../count/storeWfsFeatureCount.action";
import { BASELINE_LAYER_GROUP, WORKSPACE_LAYER_GROUP } from "../../../../common/selector/layerSelector";
import { wfsMaxFeatureCountSelector } from "../../../../common/selector/configurationSelector";
import EnvParamsRetriever from "../../../../common/util/EnvParamsRetriever";
import FilterSelector from "../../../../filter/FeatureFilter/redux/selector/FilterSelector";
import { doNothingAction } from "../../../../common/action/doNothingAction";
import Logger from "../../../../common/util/Logger";
import { saveWfs3dLayerAction } from "../save/saveWfs3dLayerAction";
import { isCesium } from "../../../../../MapStore2/web/client/selectors/maptype";
import { RELOAD_CHANGES } from "../../../../changes/redux/changeReload/reloadChangesAction";
import { FeatureCollection } from "geojson";
import { wfs3dLayerReloadAction } from "../reload/wfs3dLayerReload.action";
import { wfsService } from "../../../../api/api-packages/wfs-service/wfsService.service";

const logger = new Logger("wfs3dLayersEpic");

const getLayerRequests = (
    layerName: any,
    featureShortName: string,
    layerGroup: string,
    featureCount: number,
    maxFeatureCount: number,
    environmentParams: any
) => {
    const layerRequests = [];
    if (featureCount === 0) {
        layerRequests.push({
            layerGroup: layerGroup,
            layerName: layerName,
            featureShortName: featureShortName,
            startIndex: 0,
            count: 0,
            environmentParams: environmentParams,
        });
    }
    for (let i = 0; i < featureCount; i += maxFeatureCount) {
        layerRequests.push({
            layerGroup: layerGroup,
            layerName: layerName,
            featureShortName: featureShortName,
            startIndex: i,
            count: maxFeatureCount,
            environmentParams: environmentParams,
        });
    }

    return layerRequests;
};

function getParentFeatureShortName(
    layers: LayersState,
    wfs3dLayerName: string,
    featureToLayersMapping: FeatureLayersMappingServiceResponse
) {
    const parentLayer = layers.flat.find((layer) => layer.wfs3dLayer && layer.wfs3dLayer.includes(wfs3dLayerName));
    return Object.entries(featureToLayersMapping).filter((value) => value[1].includes(parentLayer!.name))[0][0];
}

export const wfs3dLayerLoadEpic: EpicFactory = (action$, store) =>
    action$.ofType(STORE_WFS_FEATURE_COUNT, RELOAD_CHANGES).switchMap((action) => {
        const state = store.getState();

        if (!isCesium(state)) {
            return [doNothingAction()];
        }
        const layersFeatureCount = state.wfs3dLayers.layersFeatureCount;
        if (state.wfs3dLayers.allLayersCounted) {
            const baselineLayerCount = layersFeatureCount[BASELINE_LAYER_GROUP];
            const workspaceLayerCount = layersFeatureCount[WORKSPACE_LAYER_GROUP];
            const layers: any[] = [];
            const maxFeatureCount = Math.min(
                gisServerMaxFeatureCountSelector(state) as unknown as number,
                wfsMaxFeatureCountSelector(state)
            );

            const featureToLayersMapping = state.featureLayersMapping?.receivedData;

            for (const [key, value] of Object.entries(baselineLayerCount)) {
                const environmentParams = EnvParamsRetriever.getBaselineEnvParams(state);
                const layerRequests = getLayerRequests(
                    key,
                    getParentFeatureShortName(state.layers, key, featureToLayersMapping),
                    BASELINE_LAYER_GROUP,
                    (value as any).featureCount,
                    maxFeatureCount,
                    environmentParams
                );
                layerRequests.forEach((layerRequest) => layers.push(layerRequest));
            }

            if (workspaceLayerCount) {
                for (const [key, value] of Object.entries(layersFeatureCount[WORKSPACE_LAYER_GROUP])) {
                    const layerRequests = getLayerRequests(
                        key,
                        getParentFeatureShortName(state.layers, key, featureToLayersMapping),
                        WORKSPACE_LAYER_GROUP,
                        (value as any).featureCount,
                        maxFeatureCount,
                        EnvParamsRetriever.getWorkspaceEnvParams(state)
                    );
                    layerRequests.forEach((layerRequest) => layers.push(layerRequest));
                }
            }

            return Rx.Observable.from(layers)
                .mergeMap(async (layer) => {
                    const params = {
                        typeName: layer.layerName,
                        ENV: layer.environmentParams.toRequestParams(),
                        startIndex: layer.startIndex,
                        count: layer.count,
                    };
                    return await wfsService(params)
                        .then((response) => {
                            const features = {
                                type: "FeatureCollection",
                                features: response.features,
                            };
                            return saveWfs3dLayerAction(
                                features as FeatureCollection,
                                layer.layerGroup,
                                layer.layerName,
                                layer.featureShortName,
                                FilterSelector.getDataSetFilters(state)
                            );
                        })
                        .catch((error: Error) => {
                            logger.error("Error retrieving wfs feature from GIS Server.", error);
                            return doNothingAction();
                        });
                })
                .startWith(action.type === RELOAD_CHANGES ? wfs3dLayerReloadAction() : doNothingAction());
        }
        return [doNothingAction()];
    });
